/**
 * OTRACK-SUBSYSTEM — acquired-point store (AutoCAD object-snap tracking).
 *
 * Session-level, NEVER persisted (localStorage) — acquired points are a
 * transient per-command drafting aid, exactly like `TempOverride` in
 * `dynamicInput/sysvarStore.ts`, not a durable preference. Kept as a small
 * SEPARATE zustand store (not folded into `sysvarStore` or `editorStore`)
 * because:
 *   - it is not a numeric AutoCAD sysvar (unlike `OTRACKMODE`, which DOES
 *     live in `sysvarStore` — this store only holds the acquired-point
 *     LIST, mirroring how `polarTrackingPrefs.ts` holds POLAR's non-numeric
 *     `additionalAnglesDeg` list alongside the numeric POLARANG sysvar);
 *   - it must survive undo/redo untouched (`editorStore` is transactional).
 *
 * A typed array of `OtrackAcquiredPoint`, never a boolean — AutoCAD supports
 * MULTIPLE simultaneously acquired points (combined tracking), so a single
 * "is one point acquired" flag would be a CLAUDE.md ПРИОРИТЕТ #1 violation.
 *
 * # Coordinate frame — DOCUMENT, for every writer and every reader
 *
 * `world` is a DOCUMENT (AutoCAD/DXF) coordinate. This store is written from
 * BOTH sides of the pointer pipeline and the document frame is the only one
 * they share:
 *
 *   - hover-dwell acquisition (`scene3d/pickLoop/interactivePickLoop.ts`)
 *     holds an ACTIVE-SPACE snap point and converts with that space's
 *     `ActiveInputSpace.toDocument`;
 *   - the `TT` sub-prompt (`scene/otrackTrackPointSession.ts`) receives a
 *     document point from the draft state machine and stores it verbatim;
 *   - the readers — `scene3d/snap/otrackDraftLock.otrackLockedCursor`'s
 *     alignment and the pick loop's "+" markers — convert back per sample
 *     with `ActiveInputSpace.fromDocument`.
 *
 * The ACTIVE-SPACE frame would NOT do: it is identity-branch OFFSET-world but
 * redirected-branch (MSPACE_VIA_VP) DOCUMENT, so the same `spaceId: "model"`
 * would mean two different frames, and the `TT` writer — a draft-machine
 * module with no pointer context — cannot tell the branches apart at all.
 */

import { create } from "zustand";

import type { OtrackAcquiredPoint, OtrackPoint } from "../scene3d/snap/otrackConstraint";

let nextId = 0;

/** Test-only: reset the id minter so test output is deterministic across files. */
export function __resetOtrackAcquisitionIdsForTest(): void {
  nextId = 0;
}

/** Coincidence tolerance for "is this the same acquired point" (world units). */
const COINCIDENT_EPS = 1e-6;

export interface OtrackAcquisitionState {
  readonly acquiredPoints: readonly OtrackAcquiredPoint[];
  /**
   * Toggle acquisition at `world` (captured in `spaceId` — local Codex
   * review round 5 P2, see `OtrackAcquiredPoint.spaceId`'s doc): if an
   * existing acquired point sits within `COINCIDENT_EPS` in the SAME space,
   * it is REMOVED (AutoCAD: hovering an already-acquired marker a second
   * time un-acquires it); otherwise a new point is ADDED. Two points at
   * coincident RAW numbers in DIFFERENT spaces are not the same point (a
   * paper-space (100,100) and a model-space (100,100) are unrelated
   * locations) — the coincidence check requires a `spaceId` match too.
   * Returns the resulting id (new point) or `null` (removal).
   */
  toggleAcquirePoint: (world: OtrackPoint, spaceId: string) => string | null;
  /** Remove one acquired point by id (no-op if absent). */
  removeAcquiredPoint: (id: string) => void;
  /** Clear every acquired point (command end / Escape / OTRACKMODE off). */
  clearAcquired: () => void;
}

function coincident(a: OtrackPoint, b: OtrackPoint): boolean {
  return Math.hypot(a.x - b.x, a.y - b.y) < COINCIDENT_EPS;
}

export const useOtrackAcquisitionStore = create<OtrackAcquisitionState>((set, get) => ({
  acquiredPoints: [],
  toggleAcquirePoint: (world, spaceId) => {
    const existing = get().acquiredPoints.find(
      (p) => p.spaceId === spaceId && coincident(p.world, world),
    );
    if (existing) {
      set({ acquiredPoints: get().acquiredPoints.filter((p) => p.id !== existing.id) });
      return null;
    }
    const id = `otrack-${nextId++}`;
    set({ acquiredPoints: [...get().acquiredPoints, { id, world, spaceId }] });
    return id;
  },
  removeAcquiredPoint: (id) => {
    set({ acquiredPoints: get().acquiredPoints.filter((p) => p.id !== id) });
  },
  clearAcquired: () => {
    if (get().acquiredPoints.length === 0) return;
    set({ acquiredPoints: [] });
  },
}));
